import type { DevEnv } from "../../../../types/environment.ts";
import { CREATED_BY_CLOUD_SHELL, P0_TAG_NAME } from "../constants.ts";
import type { AwsPartition } from "../util.ts";
import { rdsSecurityPerimeterPolicy, roleReaderPolicy } from "./policies.ts";
import { awsRoleName, buildGcpP0TrustPolicy } from "./shared.ts";

const toFunctionName = (vpcId: string) => `p0-rds-security-perimeter-${vpcId}`;
const toLambdaRoleName = (vpcId: string) =>
  `P0RDSPerimeterExecutionRole-${vpcId}`;
const lambdaTrustPolicy = {
  Version: "2012-10-17",
  Statement: [
    {
      Effect: "Allow",
      Principal: {
        Service: "lambda.amazonaws.com",
      },
      Action: "sts:AssumeRole",
    },
  ],
};

const lambdaDescribePolicy = {
  Version: "2012-10-17",
  Statement: [
    {
      Action: ["rds:DescribeDBInstances"],
      Effect: "Allow",
      Resource: "*",
    },
  ],
};

const version = "0.0.7";
const imageLink = "public.ecr.aws/f8o7w8j1/p0-rds-security-perimeter";
const image = `${imageLink}:${version}`;
const lambdaTrustPolicyJson = JSON.stringify(lambdaTrustPolicy, undefined, 2);

export const rdsSecurityPerimeterInstallCommands = (
  context: DevEnv,
  args: {
    awsPartition: AwsPartition;
    gcloudServiceAccountId: string;
    awsAccountId: string;
    options: { vpcId: string; region: string };
    subnetIds: string[];
  }
) => {
  const roleName = awsRoleName(context, "rds-security-perimeter");
  const { awsPartition, awsAccountId, options } = args;
  const { vpcId, region } = options;

  const policy = rdsSecurityPerimeterPolicy(args);
  return installCommands({
    ...args,
    awsPartition,
    region,
    policy,
    vpcId,
    roleName,
    awsAccountId,
  });
};

const installCommands = (args: {
  awsPartition: AwsPartition;
  gcloudServiceAccountId: string;
  policy: object;
  roleName: string;
  awsAccountId: string;
  vpcId: string;
  subnetIds: string[];
  region: string;
}) => {
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const trustPolicyJson = JSON.stringify(trustPolicy, undefined, 2);
  const { awsPartition, awsAccountId, roleName, vpcId, region, subnetIds } =
    args;
  const functionName = toFunctionName(vpcId);
  const lambdaRoleName = toLambdaRoleName(vpcId);

  const commands = `
export REGION=${region}
export ACCOUNT_ID=${awsAccountId}

VPC_ENDPOINT_SG=$(aws ec2 create-security-group \\
    --group-name vpc-endpoints-sg \\
    --description "Security group allowing all inbound traffic for VPC endpoint" \\
    --vpc-id ${vpcId} \\
    --query "GroupId" \\
    --output text) && \\
VPC_CIDR_BLOCK=$(aws ec2 describe-vpcs --vpc-ids ${vpcId} --query "Vpcs[0].CidrBlock" --output text) && \\
aws ec2 authorize-security-group-ingress \\
    --group-id $VPC_ENDPOINT_SG \\
    --protocol all \\
    --port -1 \\
    --cidr $VPC_CIDR_BLOCK \\
    --no-paginate \\
    --query "Return" --output text && \\
aws ec2 create-vpc-endpoint \\
    --vpc-id ${vpcId} \\
    --service-name com.amazonaws.${region}.rds \\
    --vpc-endpoint-type Interface \\
    --subnet-ids ${subnetIds.join(" ")} \\
    --security-group-ids $VPC_ENDPOINT_SG \\
    --region ${region} \\
    --tag-specifications 'ResourceType=vpc-endpoint,Tags=[{Key=Name,Value=p0-rds-security-perimeter-${vpcId}}]' \\
    --no-paginate \\
    --query "VpcEndpoint.VpcEndpointId" --output text

aws ecr get-login-password --region $REGION | docker login --username AWS --password-stdin $ACCOUNT_ID.dkr.ecr.$REGION.amazonaws.com

ECR_URI=$(aws ecr create-repository --repository-name p0-rds-lambda-repo --region $REGION --image-scanning-configuration scanOnPush=true --image-tag-mutability MUTABLE --query 'repository.repositoryUri' --output text)

docker pull ${image} && \
docker tag ${image} $ECR_URI:${version} && \
docker push $ECR_URI:${version}

# Create IAM role for the lambda function to assume
CALLER=$(aws sts get-caller-identity --query Arn --output text) && \\
aws iam get-role --role-name "${lambdaRoleName}" > /dev/null 2>&1 || aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${CREATED_BY_CLOUD_SHELL} by $CALLER" \\
  --role-name "${lambdaRoleName}" \\
  --assume-role-policy-document '${lambdaTrustPolicyJson}' \\
  --no-paginate \\
  --query "Role.Arn" --output text

aws iam attach-role-policy \\
  --role-name ${lambdaRoleName} \\
  --policy-arn arn:${awsPartition}:iam::aws:policy/service-role/AWSLambdaVPCAccessExecutionRole \\
  --no-paginate

aws iam put-role-policy \\
  --role-name "${lambdaRoleName}" \\
  --policy-name "P0RdsSecurityPerimeterDescribePolicy" \\
  --policy-document '${JSON.stringify(lambdaDescribePolicy, undefined, 2)}' \\
  --no-paginate

sleep 1m

LAMBDA_SG=$(aws ec2 create-security-group \\
    --group-name rds-security-perimeter-sg \\
    --description "Security group allowing traffic from Security Perimeter to RDS" \\
    --vpc-id ${vpcId} \\
    --query "GroupId" \\
    --output text) && \\
aws lambda create-function \\
    --function-name ${functionName} \\
    --code ImageUri=$ECR_URI:${version} \\
    --package-type Image \\
    --architectures arm64 \\
    --role arn:${awsPartition}:iam::${awsAccountId}:role/${lambdaRoleName} \\
    --vpc-config SubnetIds=${subnetIds.join(",")},SecurityGroupIds=$LAMBDA_SG \\
    --environment "Variables={DB_USER=p0accessmanager}" \\
    --timeout 30 \\
    --tags "Project=p0-rds-security-perimeter" \\
    --region ${region} \\
    --no-paginate \\
    --query "FunctionArn" --output text
  
while true; do
  STATE=$(aws lambda get-function-configuration \\
    --function-name ${functionName} \\
    --region ${region} \\
    --query 'State' \\
    --output text 2>/dev/null)

  if [[ "$STATE" != "Pending" ]]; then
    echo "Function is now in state: $STATE"
    break
  else
    echo "Function state is still 'Pending'. Retrying in 5 seconds..."
    sleep 5
  fi
done

# Create IAM role to call the lambda function
CALLER=$(aws sts get-caller-identity --query Arn --output text) && \\
aws iam get-role --role-name "${roleName}" > /dev/null 2>&1 || aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${CREATED_BY_CLOUD_SHELL} by $CALLER" \\
  --role-name "${roleName}" \\
  --assume-role-policy-document '${trustPolicyJson}' \\
  --query "Role.Arn" --output text

aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "P0CanReadRolesPolicy" \\
  --policy-document '${JSON.stringify(roleReaderPolicy(args), undefined, 2)}'

aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "${roleName}Policy" \\
  --policy-document '${JSON.stringify(args.policy, undefined, 2)}'

VERSION="$(aws lambda publish-version \\
  --function-name ${functionName} \\
  --description "Initial version" \\
  --query="Version" --output=text)" && \\
aws lambda put-provisioned-concurrency-config \\
  --function-name ${functionName} \\
  --qualifier $VERSION \\
  --provisioned-concurrent-executions 1 \\
  --query "Status" --output text
`;
  return commands.trim();
};

export const rdsSecurityPerimeterTerraform = (
  context: DevEnv,
  args: {
    awsPartition: AwsPartition;
    gcloudServiceAccountId: string;
    awsAccountId: string;
    options: { vpcId: string; region: string };
    subnetIds: string[];
  }
) => {
  const roleName = awsRoleName(context, "rds-security-perimeter");
  const { awsPartition, awsAccountId, options } = args;
  const { vpcId, region } = options;

  const policy = rdsSecurityPerimeterPolicy(args);
  return installTerraform({
    ...args,
    awsPartition,
    region,
    policy,
    vpcId,
    roleName,
    awsAccountId,
  });
};

const installTerraform = (args: {
  awsPartition: AwsPartition;
  gcloudServiceAccountId: string;
  policy: object;
  roleName: string;
  awsAccountId: string;
  vpcId: string;
  subnetIds: string[];
  region: string;
}) => {
  return `terraform {
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.0"
    }
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}

provider "aws" {
  region = "${args.region}"
}

locals {
  subnet_ids       = [${args.subnetIds.map((id) => `"${id}"`).join(", ")}]
  function_name    = "${toFunctionName(args.vpcId)}"
  lambda_role_name = "${toLambdaRoleName(args.vpcId)}"
  ecr_repo_name    = "p0-rds-lambda-repo"
  source_image     = "${image}"

  # Default tags for all resources
  default_tags = {
    "p0-managed"            = "true"
    "p0-vpc"                = "${args.vpcId}"
  }

  # Generate trust policy from service account ID
  trust_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect = "Allow"
      Principal = {
        Federated = "accounts.google.com"
      }
      Action = "sts:AssumeRoleWithWebIdentity"
      Condition = {
        StringEquals = {
          "accounts.google.com:aud" = "${args.gcloudServiceAccountId}"
        }
      }
    }]
  })

  # Generate inline policy from other fields
  inline_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid      = "P0CanInvokeRdsSecurityPerimeter"
      Effect   = "Allow"
      Action   = ["lambda:InvokeFunction"]
      Resource = "arn:${args.awsPartition}:lambda:${args.region}:${
        args.awsAccountId
      }:function:${toFunctionName(args.vpcId)}"
      Condition = {
        StringEquals = {
          "aws:ResourceAccount" = "${args.awsAccountId}"
        }
      }
    }]
  })

  # Role reader policy
  role_reader_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [
      {
        Sid      = "P0CanListAccountAliases",
        Effect   = "Allow",
        Action   = ["iam:ListAccountAliases"],
        Resource = "*",
        Condition = {
          StringEquals = {
            "aws:ResourceAccount" = "${args.awsAccountId}"
          }
        }
      },
      {
        Sid    = "P0CanReadOwnPolicyForValidation",
        Effect = "Allow",
        Action = [
          "iam:GetRole",
          "iam:ListRoles",
          "iam:GetRolePolicy"
        ],
        Resource = "arn:${args.awsPartition}:iam::${args.awsAccountId}:role/${
          args.roleName
        }"
        Condition = {
          StringEquals = {
            "aws:ResourceAccount" = "${args.awsAccountId}"
          }
        }
      }
    ]
  })

  lambda_trust_policy = jsonencode(${JSON.stringify(
    lambdaTrustPolicy,
    undefined,
    2
  )})

  lambda_describe_policy = jsonencode(${JSON.stringify(
    lambdaDescribePolicy,
    undefined,
    2
  )})
}

# Get VPC CIDR block
data "aws_vpc" "selected" {
  id = "${args.vpcId}"
}

# Security group for VPC endpoint
resource "aws_security_group" "vpc_endpoint" {
  name        = "vpc-endpoints-sg"
  description = "Security group allowing all inbound traffic for VPC endpoint"
  vpc_id      = "${args.vpcId}"

  ingress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = [data.aws_vpc.selected.cidr_block]
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = merge(local.default_tags, {
    Name = "p0-vpc-endpoint-sg"
  })
}

# VPC Endpoint for RDS
resource "aws_vpc_endpoint" "rds" {
  vpc_id              = "${args.vpcId}"
  service_name        = "com.amazonaws.${args.region}.rds"
  vpc_endpoint_type   = "Interface"
  subnet_ids          = local.subnet_ids
  security_group_ids  = [aws_security_group.vpc_endpoint.id]
  private_dns_enabled = true 
  tags = merge(local.default_tags, {
    Name = "p0-rds-security-perimeter-${args.vpcId}"
  })
}

# ECR Repository for Lambda image
resource "aws_ecr_repository" "lambda" {
  name                 = local.ecr_repo_name
  image_tag_mutability = "MUTABLE"

  image_scanning_configuration {
    scan_on_push = true
  }

  tags = local.default_tags
}

# Pull and push Docker image (requires docker provider and local-exec)
resource "null_resource" "docker_image" {
  triggers = {
    version       = "${version}"
    ecr_repo_name = local.ecr_repo_name
    region        = "${args.region}"
  }

  provisioner "local-exec" {
    command = <<-EOT
      aws ecr get-login-password --region ${
        args.region
      } | docker login --username AWS --password-stdin ${
        args.awsAccountId
      }.dkr.ecr.${args.region}.amazonaws.com
      docker pull ${image}
      docker tag ${image} \${aws_ecr_repository.lambda.repository_url}:${version}
      docker push \${aws_ecr_repository.lambda.repository_url}:${version}
    EOT
  }

  # delete from repository when resource is destroyed
  provisioner "local-exec" {
    when    = destroy
    command = <<-EOT
      aws ecr batch-delete-image --repository-name \${self.triggers.ecr_repo_name} --image-ids imageTag=\${self.triggers.version} --region \${self.triggers.region}
    EOT
  }

  depends_on = [aws_ecr_repository.lambda]
}

# IAM role for Lambda execution
resource "aws_iam_role" "lambda_execution" {
  name               = local.lambda_role_name
  assume_role_policy = local.lambda_trust_policy

  tags = merge(local.default_tags, {
    Name = "p0-lambda-execution-role-${args.vpcId}"
  })
}

# Attach VPC access execution role policy
resource "aws_iam_role_policy_attachment" "lambda_vpc_access" {
  role       = aws_iam_role.lambda_execution.name
  policy_arn = "arn:${
    args.awsPartition
  }:iam::aws:policy/service-role/AWSLambdaVPCAccessExecutionRole"
}

# Inline policy for RDS describe
resource "aws_iam_role_policy" "lambda_rds_describe" {
  name   = "P0RdsSecurityPerimeterDescribePolicy"
  role   = aws_iam_role.lambda_execution.id
  policy = local.lambda_describe_policy
}

# Security group for Lambda
resource "aws_security_group" "lambda" {
  name        = "rds-security-perimeter-sg"
  description = "Security group allowing traffic from Security Perimeter to RDS"
  vpc_id      = "${args.vpcId}"

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = merge(local.default_tags, {
    Name = "p0-lambda-sg-${args.vpcId}"
  })
}

# Lambda function
resource "aws_lambda_function" "rds_security_perimeter" {
  function_name = local.function_name
  role          = aws_iam_role.lambda_execution.arn
  package_type  = "Image"
  image_uri     = "\${aws_ecr_repository.lambda.repository_url}:${version}"
  architectures = ["arm64"]
  timeout       = 30
  publish       = true

  vpc_config {
    subnet_ids         = local.subnet_ids
    security_group_ids = [aws_security_group.lambda.id]
  }

  environment {
    variables = {
      DB_USER               = "p0accessmanager"
    }
  }

  tags = merge(local.default_tags, {
    Project = "p0-rds-security-perimeter"
    Name    = "p0-rds-security-perimeter-${args.vpcId}"
  })

  depends_on = [
    null_resource.docker_image,
    aws_iam_role_policy_attachment.lambda_vpc_access,
    aws_iam_role_policy.lambda_rds_describe
  ]
}

# Publish Lambda version
resource "aws_lambda_alias" "latest" {
  name             = "latest"
  function_name    = aws_lambda_function.rds_security_perimeter.function_name
  function_version = aws_lambda_function.rds_security_perimeter.version
}

# Provisioned concurrency
resource "aws_lambda_provisioned_concurrency_config" "perimeter" {
  function_name                     = aws_lambda_function.rds_security_perimeter.function_name
  provisioned_concurrent_executions = 1
  qualifier                         = aws_lambda_alias.latest.name

  depends_on = [aws_lambda_alias.latest]
}

# IAM role for invoking Lambda (main P0 role)
resource "aws_iam_role" "p0_invoke" {
  name               = "${args.roleName}"
  assume_role_policy = local.trust_policy

  tags = merge(local.default_tags, {
    Name = "p0-invoke-role-${args.vpcId}"
  })
}

# Role reader policy
resource "aws_iam_role_policy" "role_reader" {
  name   = "P0CanReadRolesPolicy"
  role   = aws_iam_role.p0_invoke.id
  policy = local.role_reader_policy
}

# Lambda invoke policy
resource "aws_iam_role_policy" "lambda_invoke" {
  name   = "${args.roleName}Policy"
  role   = aws_iam_role.p0_invoke.id
  policy = local.inline_policy
}

output "lambda_function_arn" {
  value = aws_lambda_function.rds_security_perimeter.arn
}

output "p0_role_arn" {
  value = aws_iam_role.p0_invoke.arn
}

output "vpc_endpoint_id" {
  value = aws_vpc_endpoint.rds.id
}`;
};
